import React, { FC, useEffect, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	Typography,
	Grid,
	TextField,
	IconButton,
	InputAdornment,
	Skeleton
} from "@mui/material";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import SaveIcon from "@mui/icons-material/Save";
import RestoreIcon from "@mui/icons-material/Restore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useForm, Controller } from "react-hook-form";
import ConfirmationDialog from "../../common/ConfirmationDialog";

interface HotspotFormData {
	ssid: string;
	password: string;
}

interface HotspotStatus {
	active: boolean;
	ssid?: string;
	device?: string;
	internet_sharing?: boolean;
}

const WifiHotspotConfiguration: FC = () => {
	const [hotspotStatus, setHotspotStatus] = useState<HotspotStatus | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [statusLoading, setStatusLoading] = useState<boolean>(true);
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [showResetDialog, setShowResetDialog] = useState<boolean>(false);

	const {
		control,
		handleSubmit,
		setValue,
		formState: { errors, isSubmitting }
	} = useForm<HotspotFormData>({
		defaultValues: {
			ssid: "",
			password: ""
		}
	});

	useEffect(() => {
		loadHotspotStatus();
		loadHotspotConfig();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const loadHotspotStatus = async () => {
		try {
			setStatusLoading(true);
			const response = await fetch(`http://${window.location.hostname}:81/api/hotspot/status`);
			const data = await response.json();

			if (response.ok) {
				setHotspotStatus(data);
			} else {
				throw new Error(data.error || "Failed to load hotspot status");
			}
		} catch (error) {
			console.error("Error loading hotspot status:", error);
		} finally {
			setStatusLoading(false);
		}
	};

	const loadHotspotConfig = async () => {
		try {
			const response = await fetch(`http://${window.location.hostname}:81/api/hotspot/config`);
			const data = await response.json();

			if (response.ok) {
				setValue("ssid", data.ssid || "");
				setValue("password", data.password || "");
			} else {
				throw new Error(data.error || "Failed to load hotspot configuration");
			}
		} catch (error) {
			console.error("Error loading hotspot configuration:", error);
		}
	};

	const handleHotspotToggle = async () => {
		try {
			setLoading(true);

			// First get current status
			const statusResponse = await fetch(`http://${window.location.hostname}:81/api/hotspot/status`);
			const statusData = await statusResponse.json();

			if (!statusResponse.ok) {
				throw new Error(statusData.error || "Failed to get hotspot status");
			}

			const endpoint = statusData.active ? "/api/hotspot/stop" : "/api/hotspot/start";
			const action = statusData.active ? "Stopping" : "Starting";

			const response = await fetch(`http://${window.location.hostname}:81${endpoint}`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json"
				}
			});

			const data = await response.json();

			if (response.ok) {
				alert(data.message || `${action} hotspot successful`);
				// Add a small delay before refreshing status
				setTimeout(() => {
					loadHotspotStatus();
				}, 1000);
			} else {
				throw new Error(data.error || `Failed to ${action.toLowerCase()} hotspot`);
			}
		} catch (error) {
			console.error("Error toggling hotspot:", error);
			alert(error instanceof Error ? error.message : "Error toggling hotspot");
		} finally {
			setLoading(false);
		}
	};

	const onSubmit = async (data: HotspotFormData) => {
		try {
			setLoading(true);

			if (!data.ssid.trim()) {
				alert("Network name (SSID) is required");
				return;
			}

			if (!data.password.trim()) {
				alert("Password is required");
				return;
			}

			if (data.password.length < 8) {
				alert("Password must be at least 8 characters");
				return;
			}

			const configData = {
				ssid: data.ssid.trim(),
				password: data.password.trim()
			};

			const response = await fetch(`http://${window.location.hostname}:81/api/hotspot/config`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json"
				},
				body: JSON.stringify(configData)
			});

			const result = await response.json();

			if (response.ok) {
				alert(result.message || "Hotspot configuration saved successfully");
				loadHotspotStatus(); // Refresh status
				loadHotspotConfig(); // Refresh configuration form
			} else {
				throw new Error(result.error || "Failed to update hotspot configuration");
			}
		} catch (error) {
			console.error("Error updating hotspot configuration:", error);
			alert(error instanceof Error ? error.message : "Failed to update hotspot configuration");
		} finally {
			setLoading(false);
		}
	};

	const handleHotspotReset = async () => {
		setShowResetDialog(true);
	};

	const confirmHotspotReset = async () => {
		try {
			setLoading(true);

			const response = await fetch(`http://${window.location.hostname}:81/api/hotspot/reset`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json"
				}
			});

			const data = await response.json();

			if (response.ok) {
				alert(data.message || "Hotspot configuration reset successfully");
				await loadHotspotConfig(); // Refresh configuration form
				await loadHotspotStatus(); // Refresh status
			} else {
				throw new Error(data.error || "Failed to reset hotspot configuration");
			}
		} catch (error) {
			console.error("Error resetting hotspot configuration:", error);
			alert(error instanceof Error ? error.message : "Failed to reset hotspot configuration");
		} finally {
			setLoading(false);
			setShowResetDialog(false);
		}
	};

	const togglePasswordVisibility = () => {
		setShowPassword(!showPassword);
	};

	return (
		<Box
			className="network-setting-section-wrapper"
			sx={{
				background: "#ffffff",
				borderRadius: "12px",
				boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
				border: "1px solid rgba(0, 0, 0, 0.06)"
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
				<Typography variant="h6" color="var(--color-primary-main)">
					WiFi Hotspot
				</Typography>

				<Button
					variant={hotspotStatus?.active ? "outlined" : "contained"}
					color={hotspotStatus?.active ? "error" : "success"}
					startIcon={loading ? <CircularProgress size={16} /> : <PowerSettingsNewIcon />}
					onClick={handleHotspotToggle}
					disabled={loading || statusLoading}
				>
					{loading ? "Processing..." : hotspotStatus?.active ? "Stop Hotspot" : "Start Hotspot"}
				</Button>
			</Box>

			<Box component="form" noValidate onSubmit={handleSubmit(onSubmit)} sx={{ width: "100%" }}>
				<Grid container spacing={3}>
					{/* Hotspot Status */}
					<Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
						<Box className="network-setting-wrapper column">
							<Box className="network-setting-text-wrapper">
								<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
									Hotspot Status
								</Typography>
								<Typography variant="body2" color="#616161">
									Current hotspot status and information
								</Typography>
							</Box>

							{statusLoading ? (
								<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
									{/* Status skeleton */}
									<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
										<Skeleton variant="text" width={60} height={20} />
										<Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: "12px" }} />
									</Box>

									{/* Network Name skeleton */}
									<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
										<Skeleton variant="text" width={100} height={20} />
										<Skeleton variant="text" width={120} height={20} />
									</Box>

									{/* Device skeleton */}
									<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
										<Skeleton variant="text" width={60} height={20} />
										<Skeleton variant="text" width={100} height={20} />
									</Box>

									{/* Internet Sharing skeleton */}
									<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
										<Skeleton variant="text" width={120} height={20} />
										<Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: "12px" }} />
									</Box>
								</Box>
							) : hotspotStatus ? (
								<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
									<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
										<Typography variant="body2" color="#616161">
											Status:
										</Typography>
										<Box
											sx={{
												display: "inline-flex",
												alignItems: "center",
												padding: "4px 8px",
												borderRadius: "12px",
												border: "1px solid",
												borderColor: hotspotStatus.active ? "#2e7d32" : "#666666",
												backgroundColor: hotspotStatus.active ? "rgba(46, 125, 50, 0.08)" : "rgba(102, 102, 102, 0.08)",
												minWidth: "fit-content"
											}}
										>
											<Typography
												variant="body2"
												fontWeight={500}
												color={hotspotStatus.active ? "#2e7d32" : "#666666"}
												sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}
											>
												{hotspotStatus.active ? "Active" : "Inactive"}
											</Typography>
										</Box>
									</Box>
									<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
										<Typography variant="body2" color="#616161">
											Network Name:
										</Typography>
										<Typography variant="body2" fontWeight={500}>
											{hotspotStatus.ssid || "N/A"}
										</Typography>
									</Box>
									{hotspotStatus.device && (
										<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
											<Typography variant="body2" color="#616161">
												Device:
											</Typography>
											<Typography variant="body2" fontWeight={500}>
												{hotspotStatus.device}
											</Typography>
										</Box>
									)}
									{hotspotStatus.active && (
										<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
											<Typography variant="body2" color="#616161">
												Internet Sharing:
											</Typography>
											<Box
												sx={{
													display: "inline-flex",
													alignItems: "center",
													padding: "4px 8px",
													borderRadius: "12px",
													border: "1px solid",
													borderColor: hotspotStatus.internet_sharing !== false ? "#2e7d32" : "#ed6c02",
													backgroundColor:
														hotspotStatus.internet_sharing !== false
															? "rgba(46, 125, 50, 0.08)"
															: "rgba(237, 108, 2, 0.08)",
													minWidth: "fit-content"
												}}
											>
												<Typography
													variant="body2"
													fontWeight={500}
													color={hotspotStatus.internet_sharing !== false ? "#2e7d32" : "#ed6c02"}
													sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}
												>
													{hotspotStatus.internet_sharing !== false ? "Enabled" : "Disabled"}
												</Typography>
											</Box>
										</Box>
									)}
								</Box>
							) : (
								<Typography variant="body2" color="error">
									Failed to load hotspot status
								</Typography>
							)}
						</Box>
					</Grid>

					{/* Hotspot Configuration */}
					<Grid item xs={12} md={6}>
						<Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
							<Box className="network-setting-wrapper">
								<Box className="network-setting-text-wrapper">
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										Network Name
									</Typography>
									<Typography variant="body2" color="#616161">
										Configure the network name for the hotspot
									</Typography>
								</Box>

								<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1 }}>
									{statusLoading ? (
										<Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: "4px" }} />
									) : (
										<Controller
											name="ssid"
											control={control}
											rules={{ required: "Network name is required" }}
											render={({ field }) => (
												<TextField
													{...field}
													fullWidth
													size="small"
													variant="filled"
													placeholder="Enter network name (SSID)"
													error={!!errors.ssid}
													helperText={errors.ssid?.message}
													InputProps={{
														disableUnderline: true,
														hiddenLabel: true,
														classes: { root: "filled-input-root" }
													}}
												/>
											)}
										/>
									)}
								</Box>
							</Box>

							<Box className="network-setting-wrapper">
								<Box className="network-setting-text-wrapper">
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										Password
									</Typography>
									<Typography variant="body2" color="#616161">
										Configure the password for the hotspot
									</Typography>
								</Box>

								<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1 }}>
									{statusLoading ? (
										<Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: "4px" }} />
									) : (
										<Controller
											name="password"
											control={control}
											rules={{
												required: "Password is required",
												minLength: { value: 8, message: "Password must be at least 8 characters" }
											}}
											render={({ field }) => (
												<TextField
													{...field}
													fullWidth
													size="small"
													variant="filled"
													type={showPassword ? "text" : "password"}
													placeholder="Enter password (min 8 characters)"
													error={!!errors.password}
													helperText={errors.password?.message}
													InputProps={{
														disableUnderline: true,
														hiddenLabel: true,
														classes: { root: "filled-input-root" },
														endAdornment: (
															<InputAdornment position="end">
																<IconButton onClick={togglePasswordVisibility} edge="end">
																	{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
																</IconButton>
															</InputAdornment>
														)
													}}
												/>
											)}
										/>
									)}
								</Box>
							</Box>
						</Box>
					</Grid>

					<Grid item xs={12}>
						<Box sx={{ display: "flex", gap: 2 }}>
							<Button
								type="submit"
								variant="contained"
								color="primary"
								startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
								disabled={loading || isSubmitting}
							>
								{loading ? "Saving..." : "Save Configuration"}
							</Button>
							<Button
								type="button"
								variant="outlined"
								color="warning"
								startIcon={<RestoreIcon />}
								onClick={handleHotspotReset}
								disabled={loading}
							>
								Reset
							</Button>
						</Box>
					</Grid>
				</Grid>
			</Box>

			<ConfirmationDialog
				width="sm"
				open={showResetDialog}
				title="Reset Hotspot Configuration"
				heading="Are you sure you want to reset the hotspot configuration to defaults?"
				warning="SSID: GateGuard\nPassword: 123456789"
				onClose={() => setShowResetDialog(false)}
				onConfirm={confirmHotspotReset}
			/>
		</Box>
	);
};

export default WifiHotspotConfiguration;
